import {meService} from "@/src/modules/core/services/me";
import type {GetListResponse, ParamsOptions} from "@/src/modules/core/types";
import {propertiesService} from "@/src/modules/properties/services";

import {patrolRoutesApi} from "../api";
import type {
  CreatePatrolRouteInput,
  PatrolRoute,
  PatrolRouteListItem,
  UpdatePatrolRouteInput,
} from "../types";
import {patrolRoutesSerializer} from "./serializer";

/**
 * Show may return visits with only `property_id`. Resolve names in one
 * `filter[ids]` properties call so the edit sheet does not depend on the
 * combobox's first infinite page (or `Property #N` stubs).
 */
async function hydrateVisitProperties(route: PatrolRoute): Promise<PatrolRoute> {
  const visits = Array.isArray(route.visits) ? route.visits : [];
  const missingIds = [
    ...new Set(
      visits
        .filter((visit) => !visit.property?.name?.trim())
        .map((visit) => visit.property_id)
        .filter((id): id is number => typeof id === "number" && id > 0),
    ),
  ];

  if (missingIds.length === 0) return route;

  const {data: properties} = await propertiesService.getProperties({
    sort: "name",
    filter: {ids: missingIds.join(",")},
    page: {limit: missingIds.length, offset: 0},
  });
  const nameById = new Map(properties.map((property) => [property.id, property.name]));

  return {
    ...route,
    visits: visits.map((visit) => {
      if (visit.property?.name?.trim()) return visit;
      const name = nameById.get(visit.property_id);
      if (!name) return visit;
      return {
        ...visit,
        property: {id: visit.property_id, name},
      };
    }),
  };
}

class PatrolRoutesService {
  async list(options: ParamsOptions): Promise<GetListResponse<PatrolRouteListItem>> {
    const {data} = await patrolRoutesApi.list(options);
    const rows = data.data;
    const meta = data.meta;
    return {
      data: Array.isArray(rows) ? rows : [],
      meta: {total: meta?.total ?? 0, from: meta?.from ?? 0, to: meta?.to ?? 0},
    };
  }

  async getById(id: number): Promise<PatrolRoute> {
    const {data} = await patrolRoutesApi.show(id);
    return hydrateVisitProperties(data.data);
  }

  async create(input: CreatePatrolRouteInput): Promise<PatrolRoute> {
    const {data} = await patrolRoutesApi.store(patrolRoutesSerializer.serializeForm(input));
    return data.data;
  }

  async update(id: number, input: UpdatePatrolRouteInput): Promise<PatrolRoute> {
    const {data} = await patrolRoutesApi.patch(
      id,
      patrolRoutesSerializer.serializeForm(input, {persistColor: true}),
    );
    return data.data;
  }

  async deactivate(id: number): Promise<void> {
    await patrolRoutesApi.deactivate(id);
  }

  getIsListEnabled(): boolean {
    return meService.getHasPermission("patrol-route list");
  }
  /** Detail modal / Eye — `show`, or `list` when show is not granted separately. */
  getIsShowEnabled(): boolean {
    return (
      meService.getHasPermission("patrol-route show") ||
      meService.getHasPermission("patrol-route list")
    );
  }
  getIsCreateEnabled(): boolean {
    return meService.getHasPermission("patrol-route store");
  }
  getIsUpdateEnabled(): boolean {
    return meService.getHasPermission("patrol-route update");
  }
  getIsDeactivateEnabled(): boolean {
    return meService.getHasPermission("patrol-route delete");
  }
}

export const patrolRoutesService = new PatrolRoutesService();
